import {HTTP} from "@/http";

class CustomerService {
  basePath = 'api/v2/customers';

  /**
   * Get all customers (for admin/reporter)
   * @returns {Promise<Object>} Promise that resolves to the customers response
   */
  get() {
    return HTTP.get(this.basePath);
  }

  /**
   * Get customer by ID
   * @param {string} id - The customer ID
   * @returns {Promise<Object>} Promise that resolves to the customer
   */
  getById(id) {
    return HTTP.get(`${this.basePath}/${id}`);
  }

  /**
   * Delete a customer
   * @param {string} id - The customer ID to delete
   * @returns {Promise<Object>} Promise that resolves when the customer is deleted
   */
  delete(id) {
    return HTTP.delete(`${this.basePath}/${id}`);
  }

  /**
   * Update a customer
   * @param {Object} customer - The customer to update
   * @returns {Promise<Object>} Promise that resolves to the updated customer
   */
  patch(customer) {
    return HTTP.patch(`${this.basePath}/${customer.id}`, customer);
  }

  addAdConnection(customer, adConnection) {
    return HTTP.post(`${this.basePath}/${customer.id}/adConnection`, adConnection);
  }

  deleteAdConnection(customer, adConnection) {
    return HTTP.delete(`${this.basePath}/${customer.id}/adConnection/${adConnection.id}`);
  }

  updateAdConnection(customer, adConnection) {
    return HTTP.put(`${this.basePath}/${customer.id}/adConnection/${adConnection.id}`, adConnection);
  }

  /**
   * Synchronize accounts for a customer
   * @param {string} id - The customer ID
   * @returns {Promise<Object>} Promise that resolves when the accounts are synced
   */
  syncAccounts(id) {
    return HTTP.post(`/api/v2/admin/remote-sync/customer/${id}`, null, {timeout: 600000});
  }

  connectAD(data) {
    return HTTP.post(`/api/v2/admin/ldap/connection`, data, {timeout: 600000});
  }

  /**
   * Create a new customer
   * @param {Object} customer - The customer to create
   * @returns {Promise<Object>} Promise that resolves to the created customer
   */
  create(customer) {
    if (customer.adConnections === undefined) {
      customer.adConnections = [];
    }
    return HTTP.post(this.basePath, customer);
  }

  /**
   * Get accounts for a specific customer
   * @param {string} customerId - The customer ID
   * @param {Object} pageParams - Optional pagination parameters
   * @returns {Promise<Object>} Promise that resolves to the paginated accounts
   */
  getAccounts(customerId, pageParams = {}) {
    return HTTP.get(`${this.basePath}/${customerId}/accounts`, {params: pageParams});
  }

  /**
   * Add account to a customer
   * @param {string} customerId - Customer ID
   * @param {Object} account - Account data
   * @returns {Promise<Object>} Promise that resolves when the account is added
   */
  addAccount(customerId, account) {
    return HTTP.post(`${this.basePath}/${customerId}/accounts`, account);
  }

  /**
   * Assign an existing account to a customer
   * @param {string} customerId - Customer ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Promise that resolves when the account is assigned
   */
  assignAccount(customerId, accountId) {
    return HTTP.post(`${this.basePath}/${customerId}/accounts/${accountId}`);
  }
}

/**
 * @type {CustomerService}
 */
export const customerService = new CustomerService();
